import { gql } from '@apollo/client';
import { mean, orderBy } from 'lodash';
import { INVERTED_REVENUE_UNITS_BY_DEFAULT } from '../../constants/units';
import { MULTI_YEAR } from '../../constants/years';
import { useUnitsMap } from '../../contexts/UnitContext';
import { asCurrency, asPercentage, asUnitPrice } from '../../utils/formatters';
import ExportDashboardYearOrYearsButton, {
  sliceYearArray,
} from './ExportDashboardYearOrYearsButton';
import { getRevenueStreamData } from '../../utils/transforms';

const revenueQuery = gql`
  query ProjectListRevenueQuery($ids: [ID!]) {
    farms(ids: $ids) {
      id
      name
      startYear
      cropAggregates {
        crop {
          id
          name
        }
        revenueStreamAggregates {
          revenueStream {
            id
            name
            filledPercent
            filledUnitCost
            lbsPerUnit
            unit
          }
          revenueByYear
        }
      }
    }
  }
`;

// given a format function and a default value
// return a function that formats a subset of array values
// or returns the default value if indices are out of bounds
const getMultiYearValueOrDefault = (formatFn, defaultVal) => (
  arr,
  { startYearIndex, endYearIndex, ...row },
) => {
  // clip year indices to be within the length of the array
  const includedYears = sliceYearArray(arr, startYearIndex, endYearIndex);
  if (includedYears.length === 0) return defaultVal;

  return formatFn(includedYears, row);
};

// given a format function and a default value
// return a function that formats a single year value
// returns the default value if the year index is out of bounds
const getSingleYearValueOrDefault = (formatFn, defaultVal) => (
  arr,
  { startYearIndex, ...row },
) =>
  startYearIndex < 0 || startYearIndex >= arr.length
    ? defaultVal
    : formatFn(arr[startYearIndex], row);

const revenueColumns = (year, years, startYear) => {
  const yearText =
    year === MULTI_YEAR ? `${startYear}-${startYear + years - 1}` : year;
  return [
    { displayString: 'Farm Name', accessor: 'farmName' },
    { displayString: 'Crop', accessor: (row) => row?.crop?.name },
    {
      displayString: 'Revenue Stream',
      accessor: 'name',
    },
    ...(year === MULTI_YEAR
      ? [
          { displayString: 'Year Range', accessor: () => yearText },
          {
            displayString: `Avg % Allocated to Stream`,
            accessor: 'filledPercent',
            format: getMultiYearValueOrDefault(
              (arr) => asPercentage(mean(arr)),
              '-',
            ),
          },
          {
            displayString: `Avg Price`,
            accessor: 'filledUnitCost',
            format: getMultiYearValueOrDefault(
              (arr, row) => asUnitPrice(mean(arr), row),
              '-',
            ),
          },
          {
            displayString: `Max Revenue`,
            accessor: 'revenueByYear',
            format: getMultiYearValueOrDefault(
              (arr) => asCurrency(Math.max(...arr)),
              '-',
            ),
          },
        ]
      : []),
    ...(year !== MULTI_YEAR
      ? [
          { displayString: 'Year', accessor: () => yearText },
          {
            displayString: `% Allocated to Stream`,
            accessor: 'filledPercent',
            format: getSingleYearValueOrDefault(asPercentage, '-'),
          },
          {
            displayString: `Price`,
            accessor: 'filledUnitCost',
            format: getSingleYearValueOrDefault(asUnitPrice, '-'),
          },
          {
            displayString: `Revenue`,
            accessor: 'revenueByYear',
            format: getSingleYearValueOrDefault(asCurrency, '-'),
          },
        ]
      : []),
  ];
};

const revenueDataTransform = (invertedRevenueUnitMap) => ({ cropAggregates }) =>
  orderBy(
    cropAggregates.flatMap((cropAggregate) =>
      getRevenueStreamData(invertedRevenueUnitMap)(cropAggregate).map(
        (revStreamData) => ({
          crop: cropAggregate.crop,
          ...revStreamData,
        }),
      ),
    ),
    ['crop.name'],
  );

const ExportDashboardRevenueButton = ({ year, years, startYear, ...props }) => {
  const invertedRevenueUnitMap = useUnitsMap(INVERTED_REVENUE_UNITS_BY_DEFAULT);

  return (
    <ExportDashboardYearOrYearsButton
      columns={revenueColumns(year, years, startYear)}
      dataTransform={revenueDataTransform(invertedRevenueUnitMap)}
      query={revenueQuery}
      name="revenue"
      year={year}
      years={years}
      startYear={startYear}
      {...props}
    />
  );
};

export default ExportDashboardRevenueButton;
